// Written by scripts/generate.ts from schema/. Change the schema, then run `npm run generate`.
import type {
  BTR_FIREWALL_STATUS,
  COMMAND_REPLY_TYPES,
  CTF_FLAG_STATE,
  CTF_TEAMS,
  ClockTime,
  GAME_TYPES,
  LEAVE_HORIZON_TYPES,
  MOB_DESPAWN_TYPES,
  MOB_TYPES,
  PLANE_TYPES,
  PLAYER_LEVEL_UPDATE_TYPES,
  PLAYER_POWERUP_TYPES,
  PLAYER_STATUS,
  PLAYER_UPGRADE_TYPES,
  SERVER_CUSTOM_TYPES,
  SERVER_ERRORS,
  SERVER_MESSAGE_TYPES,
  TeamId,
} from '../values.ts';

/** The codes of the packets a server sends. */
export const SERVER_PACKETS = {
  LOGIN: 0,
  BACKUP: 1,
  PING: 5,
  PING_RESULT: 6,
  ACK: 7,
  ERROR: 8,
  COMMAND_REPLY: 9,
  PLAYER_NEW: 10,
  PLAYER_LEAVE: 11,
  PLAYER_UPDATE: 12,
  PLAYER_FIRE: 13,
  PLAYER_HIT: 14,
  PLAYER_RESPAWN: 15,
  PLAYER_FLAG: 16,
  PLAYER_KILL: 17,
  PLAYER_UPGRADE: 18,
  PLAYER_TYPE: 19,
  PLAYER_POWERUP: 20,
  PLAYER_LEVEL: 21,
  PLAYER_RETEAM: 22,
  GAME_FLAG: 30,
  GAME_SPECTATE: 31,
  GAME_PLAYERSALIVE: 32,
  GAME_FIREWALL: 33,
  EVENT_REPEL: 40,
  EVENT_BOOST: 41,
  EVENT_BOUNCE: 42,
  EVENT_STEALTH: 43,
  EVENT_LEAVEHORIZON: 44,
  MOB_UPDATE: 60,
  MOB_UPDATE_STATIONARY: 61,
  MOB_DESPAWN: 62,
  MOB_DESPAWN_COORDS: 63,
  CHAT_PUBLIC: 70,
  CHAT_TEAM: 71,
  CHAT_SAY: 72,
  CHAT_WHISPER: 73,
  CHAT_VOTEMUTEPASSED: 78,
  CHAT_VOTEMUTED: 79,
  SCORE_UPDATE: 80,
  SCORE_BOARD: 81,
  SCORE_DETAILED: 82,
  SCORE_DETAILED_CTF: 83,
  SCORE_DETAILED_BTR: 84,
  SERVER_MESSAGE: 90,
  SERVER_CUSTOM: 91,
  SYNC_AUTH: 201,
  SYNC_INIT: 202,
  SYNC_SUBSCRIBE: 203,
  SYNC_UPDATE: 204,
} as const;

/** Any packet code a server sends. */
export type ServerPacketCode = (typeof SERVER_PACKETS)[keyof typeof SERVER_PACKETS];

// =============================================================================
// Login and connection
// =============================================================================

/** A player in the room, as the LOGIN packet lists them. */
export interface LoginPlayer {
  /** Player ID */
  id: number;
  /** Alive on the map, or inactive: dead or spectating */
  status: PLAYER_STATUS;
  /** Account level */
  level: number;
  /** Player name, unique in the room */
  name: string;
  /** Airplane type */
  type: PLANE_TYPES;
  /** Team ID */
  team: TeamId;
  /** Position X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Position Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
  /** Rotation, from 0 to 10 */
  rot: number;
  /** Country flag code */
  flag: number;
  /** Encoded upgrades */
  upgrades: number;
}

/** A bot's id. The bot is in the players list too. */
export interface LoginBot {
  /** Bot player ID */
  id: number;
}

/** The reply to a LOGIN: the player id and team, the server time, the backup token, the room and everyone in it. */
export interface ServerLoginMessage {
  c: typeof SERVER_PACKETS.LOGIN;
  /** Success flag (currently not used) */
  success: boolean;
  /** Assigned player ID */
  id: number;
  /** Assigned team ID */
  team: TeamId;
  /** Server time of the packet */
  clock: ClockTime;
  /** Token for the backup connection */
  token: string;
  /** The room's game type (6.2.0's declarations call it the airplane type) */
  type: GAME_TYPES;
  /** Room name */
  room: string;
  /** Everyone in the room, this player and the bots included */
  players: LoginPlayer[];
  /** Server configuration as a JSON string */
  serverConfiguration: string;
  /** The IDs of the bots among the players */
  bots: LoginBot[];
}

/** Confirms the backup connection; an empty packet. */
export interface ServerBackupMessage {
  c: typeof SERVER_PACKETS.BACKUP;
}

/** Asks for a PONG, to measure the round trip. */
export interface ServerPingMessage {
  c: typeof SERVER_PACKETS.PING;
  /** Server time of the packet */
  clock: ClockTime;
  /** Random number the PONG returns */
  num: number;
}

/** The measured ping and the player counts. */
export interface ServerPingResultMessage {
  c: typeof SERVER_PACKETS.PING_RESULT;
  /** Player ping in milliseconds */
  ping: number;
  /** Players across the server group */
  playerstotal: number;
  /** Players on this server */
  playersgame: number;
}

/** Acknowledgment; an empty packet (currently not used). */
export interface ServerAckMessage {
  c: typeof SERVER_PACKETS.ACK;
}

/** An error the player should know of: a ban, a kick, a refused action. It does not disconnect by itself. */
export interface ServerErrorMessage {
  c: typeof SERVER_PACKETS.ERROR;
  /** Error code */
  error: SERVER_ERRORS;
}

/** The reply to a COMMAND. */
export interface ServerCommandReplyMessage {
  c: typeof SERVER_PACKETS.COMMAND_REPLY;
  /** CHAT for a line in the chat, DEBUG for a popup */
  type: COMMAND_REPLY_TYPES;
  /** Reply text */
  text: string;
}

// =============================================================================
// Players
// =============================================================================

/** A player joined the room. */
export interface ServerPlayerNewMessage {
  c: typeof SERVER_PACKETS.PLAYER_NEW;
  /** Player ID */
  id: number;
  /** Alive on the map, or inactive: dead or spectating */
  status: PLAYER_STATUS;
  /** Player name */
  name: string;
  /** Airplane type */
  type: PLANE_TYPES;
  /** Team ID */
  team: TeamId;
  /** Position X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Position Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
  /** Rotation, from 0 to 10 */
  rot: number;
  /** Country flag code */
  flag: number;
  /** Encoded upgrades */
  upgrades: number;
  /** Whether the player is a bot */
  isBot: boolean;
}

/** A player left the room. */
export interface ServerPlayerLeaveMessage {
  c: typeof SERVER_PACKETS.PLAYER_LEAVE;
  /** Player ID */
  id: number;
}

/** A player's position and motion, sent often for players in view so the client can interpolate. */
export interface ServerPlayerUpdateMessage {
  c: typeof SERVER_PACKETS.PLAYER_UPDATE;
  /** Server time of the packet */
  clock: ClockTime;
  /** Player ID */
  id: number;
  /** Encoded keystate: the keys held down, and the boost, strafe, stealth and flag-speed bits */
  keystate: number;
  /** Encoded upgrades */
  upgrades: number;
  /** Position X, from -16384 to 16384 */
  posX: number;
  /** Position Y, from -16384 to 16384 */
  posY: number;
  /** Rotation, from 0 to 10 */
  rot: number;
  /** Velocity X, from -20 to 20 */
  speedX: number;
  /** Velocity Y, from -20 to 20 */
  speedY: number;
}

/** One projectile of a PLAYER_FIRE. */
export interface PlayerFireProjectile {
  /** Projectile ID */
  id: number;
  /** Projectile type */
  type: MOB_TYPES;
  /** Position X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Position Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
  /** Velocity X at launch (depends on the player's velocity and direction), from -20 to 20 */
  speedX: number;
  /** Velocity Y at launch, from -20 to 20 */
  speedY: number;
  /** Acceleration X (depends on the owner's upgrades), from -1 to 1 */
  accelX: number;
  /** Acceleration Y, from -1 to 1 */
  accelY: number;
  /** Maximum speed, from -20 to 20 */
  maxSpeed: number;
}

/** A player fired: their energy after it and every projectile the shot spawned. */
export interface ServerPlayerFireMessage {
  c: typeof SERVER_PACKETS.PLAYER_FIRE;
  /** Server time of the packet */
  clock: ClockTime;
  /** Player ID */
  id: number;
  /** Energy after firing, from 0 to 1 in steps of 1/255 */
  energy: number;
  /** Energy regeneration rate (depends on upgrades), from -0.032768 to 0.032767 */
  energyRegen: number;
  /** The projectiles fired */
  projectiles: PlayerFireProjectile[];
}

/** One player a projectile hit, with their health after it. */
export interface PlayerHitPlayer {
  /** Player ID */
  id: number;
  /** Health after the hit, from 0 to 1 in steps of 1/255 */
  health: number;
  /** Health regeneration rate, from -0.032768 to 0.032767 */
  healthRegen: number;
}

/** A projectile or the BTR firewall hit one or more players. */
export interface ServerPlayerHitMessage {
  c: typeof SERVER_PACKETS.PLAYER_HIT;
  /** Projectile ID */
  id: number;
  /** Projectile type, or the BTR firewall */
  type: MOB_TYPES;
  /** Impact position X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Impact position Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
  /** Projectile owner's player ID, or 0 for the BTR firewall */
  owner: number;
  /** The players hit */
  players: PlayerHitPlayer[];
}

/** A player respawned. */
export interface ServerPlayerRespawnMessage {
  c: typeof SERVER_PACKETS.PLAYER_RESPAWN;
  /** Player ID */
  id: number;
  /** Spawn position X, from -16384 to 16384 */
  posX: number;
  /** Spawn position Y, from -16384 to 16384 */
  posY: number;
  /** Rotation, from 0 to 10 */
  rot: number;
  /** Encoded upgrades */
  upgrades: number;
}

/** A player changed their country flag. */
export interface ServerPlayerFlagMessage {
  c: typeof SERVER_PACKETS.PLAYER_FLAG;
  /** Player ID */
  id: number;
  /** Country flag code */
  flag: number;
}

/** A player died, or left the map to spectate or change plane (killer and position 0, sent to them alone). */
export interface ServerPlayerKillMessage {
  c: typeof SERVER_PACKETS.PLAYER_KILL;
  /** Player ID */
  id: number;
  /** Killer's player ID; 0 for a death no player caused, such as the BTR firewall's */
  killer: number;
  /** Position X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Position Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
}

/** Sent to a player whose upgrades changed, with the counts after it. */
export interface ServerPlayerUpgradeMessage {
  c: typeof SERVER_PACKETS.PLAYER_UPGRADE;
  /** Free upgrades available */
  upgrades: number;
  /** The kind just applied, or LOST when the counts changed without one */
  type: PLAYER_UPGRADE_TYPES;
  /** Speed upgrades applied */
  speed: number;
  /** Defense upgrades applied */
  defense: number;
  /** Energy upgrades applied */
  energy: number;
  /** Missile upgrades applied */
  missile: number;
}

/** A player switched airplane. */
export interface ServerPlayerTypeMessage {
  c: typeof SERVER_PACKETS.PLAYER_TYPE;
  /** Player ID */
  id: number;
  /** Airplane type */
  type: PLANE_TYPES;
}

/** Sent to a player who picked up a shield or an inferno. */
export interface ServerPlayerPowerupMessage {
  c: typeof SERVER_PACKETS.PLAYER_POWERUP;
  /** Shield or inferno */
  type: PLAYER_POWERUP_TYPES;
  /** Duration in milliseconds */
  duration: number;
}

/** A player's account level: told on login, or a level up. */
export interface ServerPlayerLevelMessage {
  c: typeof SERVER_PACKETS.PLAYER_LEVEL;
  /** Player ID */
  id: number;
  /** Told on login, or a level up */
  type: PLAYER_LEVEL_UPDATE_TYPES;
  /** Level, above 0 */
  level: number;
}

/** One player's new team. */
export interface PlayerReteamPlayer {
  /** Player ID */
  id: number;
  /** New team ID */
  team: TeamId;
}

/** One or more players moved to another team. */
export interface ServerPlayerReteamMessage {
  c: typeof SERVER_PACKETS.PLAYER_RETEAM;
  /** The players moved */
  players: PlayerReteamPlayer[];
}

// =============================================================================
// Game modes
// =============================================================================

/** A CTF flag's position or carrier, with both teams' scores. */
export interface ServerGameFlagMessage {
  c: typeof SERVER_PACKETS.GAME_FLAG;
  /** Resting or dropped (STATIC), or carried (DYNAMIC) */
  type: CTF_FLAG_STATE;
  /** The flag's team */
  flag: CTF_TEAMS;
  /** Carrier's player ID, when carried */
  id: number;
  /** Position X, from -16384 to 16384 */
  posX: number;
  /** Position Y, from -16384 to 16384 */
  posY: number;
  /** Blue team score */
  blueteam: number;
  /** Red team score */
  redteam: number;
}

/** The player now spectates, or watches someone else. */
export interface ServerGameSpectateMessage {
  c: typeof SERVER_PACKETS.GAME_SPECTATE;
  /** Player ID of the one watched */
  id: number;
}

/** How many players are still alive in a BTR match. */
export interface ServerGamePlayersAliveMessage {
  c: typeof SERVER_PACKETS.GAME_PLAYERSALIVE;
  /** Players alive */
  players: number;
}

/** The BTR firewall's centre, radius and speed. */
export interface ServerGameFirewallMessage {
  c: typeof SERVER_PACKETS.GAME_FIREWALL;
  /** Type (currently not used) */
  type: number;
  /** Firewall status: INACTIVE at a match's end */
  status: BTR_FIREWALL_STATUS;
  /** Centre X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Centre Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
  /** Radius of the safe circle, in map units; players outside it burn */
  radius: number;
  /** Rate the radius changes, in map units per second; negative while the circle shrinks */
  speed: number;
}

// =============================================================================
// Events
// =============================================================================

/** A player the repel threw, with their state after it. */
export interface EventRepelPlayer {
  /** Player ID */
  id: number;
  /** Encoded keystate */
  keystate: number;
  /** Position X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Position Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
  /** Rotation, from 0 to 10 */
  rot: number;
  /** Velocity X, from -20 to 20 */
  speedX: number;
  /** Velocity Y, from -20 to 20 */
  speedY: number;
  /** Energy, from 0 to 1 in steps of 1/255 */
  energy: number;
  /** Energy regeneration rate, from -0.032768 to 0.032767 */
  energyRegen: number;
  /** Health, from 0 to 1 in steps of 1/255 */
  playerHealth: number;
  /** Health regeneration rate, from -0.032768 to 0.032767 */
  playerHealthRegen: number;
}

/** A projectile the repel threw, with its motion after it. */
export interface EventRepelMob {
  /** Projectile ID */
  id: number;
  /** Projectile type */
  type: MOB_TYPES;
  /** Position X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Position Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
  /** Velocity X, from -20 to 20 */
  speedX: number;
  /** Velocity Y, from -20 to 20 */
  speedY: number;
  /** Acceleration X, from -1 to 1 */
  accelX: number;
  /** Acceleration Y, from -1 to 1 */
  accelY: number;
  /** Maximum speed, from -20 to 20 */
  maxSpeed: number;
}

/** A Goliath's repel, with the players and projectiles it threw. */
export interface ServerEventRepelMessage {
  c: typeof SERVER_PACKETS.EVENT_REPEL;
  /** Server time of the packet */
  clock: ClockTime;
  /** Repeller's player ID */
  id: number;
  /** Position X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Position Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
  /** Rotation, from 0 to 10 */
  rot: number;
  /** Velocity X, from -20 to 20 */
  speedX: number;
  /** Velocity Y, from -20 to 20 */
  speedY: number;
  /** Energy after the repel, from 0 to 1 in steps of 1/255 */
  energy: number;
  /** Energy regeneration rate, from -0.032768 to 0.032767 */
  energyRegen: number;
  /** The players thrown */
  players: EventRepelPlayer[];
  /** The projectiles thrown */
  mobs: EventRepelMob[];
}

/** A Predator started or stopped boosting. */
export interface ServerEventBoostMessage {
  c: typeof SERVER_PACKETS.EVENT_BOOST;
  /** Server time of the packet */
  clock: ClockTime;
  /** Player ID */
  id: number;
  /** Boosting */
  boost: boolean;
  /** Position X, from -16384 to 16384 */
  posX: number;
  /** Position Y, from -16384 to 16384 */
  posY: number;
  /** Rotation, from 0 to 10 */
  rot: number;
  /** Velocity X, from -20 to 20 */
  speedX: number;
  /** Velocity Y, from -20 to 20 */
  speedY: number;
  /** Energy, from 0 to 1 in steps of 1/255 */
  energy: number;
  /** Energy regeneration rate, from -0.032768 to 0.032767 */
  energyRegen: number;
}

/** A player bounced off the terrain. */
export interface ServerEventBounceMessage {
  c: typeof SERVER_PACKETS.EVENT_BOUNCE;
  /** Server time of the packet */
  clock: ClockTime;
  /** Player ID */
  id: number;
  /** Encoded keystate */
  keystate: number;
  /** Position X after the bounce, from -16384 to 16384 */
  posX: number;
  /** Position Y after the bounce, from -16384 to 16384 */
  posY: number;
  /** Rotation, from 0 to 10 */
  rot: number;
  /** Velocity X, from -20 to 20 */
  speedX: number;
  /** Velocity Y, from -20 to 20 */
  speedY: number;
}

/** A Prowler entered or left stealth. */
export interface ServerEventStealthMessage {
  c: typeof SERVER_PACKETS.EVENT_STEALTH;
  /** Player ID */
  id: number;
  /** Stealthed */
  state: boolean;
  /** Energy, from 0 to 1 in steps of 1/255 */
  energy: number;
  /** Energy regeneration rate, from -0.032768 to 0.032767 */
  energyRegen: number;
}

/** A player or mob left the client's view. */
export interface ServerEventLeaveHorizonMessage {
  c: typeof SERVER_PACKETS.EVENT_LEAVEHORIZON;
  /** Whether a player or a mob left */
  type: LEAVE_HORIZON_TYPES;
  /** Player or mob ID */
  id: number;
}

// =============================================================================
// Mobs: projectiles and boxes
// =============================================================================

/** A moving projectile appeared or changed course. */
export interface ServerMobUpdateMessage {
  c: typeof SERVER_PACKETS.MOB_UPDATE;
  /** Server time of the packet */
  clock: ClockTime;
  /** Projectile ID */
  id: number;
  /** Projectile type */
  type: MOB_TYPES;
  /** Position X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Position Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
  /** Velocity X, from -20 to 20 */
  speedX: number;
  /** Velocity Y, from -20 to 20 */
  speedY: number;
  /** Acceleration X, from -1 to 1 */
  accelX: number;
  /** Acceleration Y, from -1 to 1 */
  accelY: number;
  /** Maximum speed, from -20 to 20 */
  maxSpeed: number;
  /** Owner's player ID, or 0 */
  ownerId: number;
}

/** A box (an upgrade or a powerup) appeared. */
export interface ServerMobUpdateStationaryMessage {
  c: typeof SERVER_PACKETS.MOB_UPDATE_STATIONARY;
  /** Mob ID */
  id: number;
  /** Mob type */
  type: MOB_TYPES;
  /** Position X */
  posX: number;
  /** Position Y */
  posY: number;
}

/** A mob is gone: expired or picked up. The player who picks a box up gets PICKUP, and everyone else EXPIRED. */
export interface ServerMobDespawnMessage {
  c: typeof SERVER_PACKETS.MOB_DESPAWN;
  /** Mob ID */
  id: number;
  /** Why it went */
  type: MOB_DESPAWN_TYPES;
}

/** A projectile hit the terrain, and where. */
export interface ServerMobDespawnCoordsMessage {
  c: typeof SERVER_PACKETS.MOB_DESPAWN_COORDS;
  /** Projectile ID */
  id: number;
  /** Projectile type */
  type: MOB_TYPES;
  /** Impact position X, from -16384 to 16384 in steps of 0.5 */
  posX: number;
  /** Impact position Y, from -8192 to 8192 in steps of 0.25 */
  posY: number;
}

// =============================================================================
// Chat
// =============================================================================

/** A chat line for everyone. */
export interface ServerChatPublicMessage {
  c: typeof SERVER_PACKETS.CHAT_PUBLIC;
  /** Sender's player ID */
  id: number;
  /** Message text */
  text: string;
}

/** A chat line for the sender's team. */
export interface ServerChatTeamMessage {
  c: typeof SERVER_PACKETS.CHAT_TEAM;
  /** Sender's player ID */
  id: number;
  /** Message text */
  text: string;
}

/** A speech bubble over the sender's airplane. */
export interface ServerChatSayMessage {
  c: typeof SERVER_PACKETS.CHAT_SAY;
  /** Sender's player ID */
  id: number;
  /** Message text */
  text: string;
}

/** A private message between two players. */
export interface ServerChatWhisperMessage {
  c: typeof SERVER_PACKETS.CHAT_WHISPER;
  /** Sender's player ID */
  from: number;
  /** Recipient's player ID */
  to: number;
  /** Message text */
  text: string;
}

/** A vote muted a player. */
export interface ServerChatVoteMutePassedMessage {
  c: typeof SERVER_PACKETS.CHAT_VOTEMUTEPASSED;
  /** Muted player's ID */
  id: number;
}

/** Sent to a muted player who tries to chat; an empty packet. */
export interface ServerChatVoteMutedMessage {
  c: typeof SERVER_PACKETS.CHAT_VOTEMUTED;
}

// =============================================================================
// Scores
// =============================================================================

/** The player's score, after a kill, a death, a level up or an upgrade picked up. */
export interface ServerScoreUpdateMessage {
  c: typeof SERVER_PACKETS.SCORE_UPDATE;
  /** Player ID */
  id: number;
  /** Score */
  score: number;
  /** Total earnings (accounts only) */
  earnings: number;
  /** Free upgrades available */
  upgrades: number;
  /** Total kills */
  totalkills: number;
  /** Total deaths */
  totaldeaths: number;
}

/** One player's score and level on the scoreboard. */
export interface ScoreBoardData {
  /** Player ID */
  id: number;
  /** Score */
  score: number;
  /** Level */
  level: number;
}

/** One player's low-resolution position for the minimap; 0,0 while dead or spectating. */
export interface ScoreBoardRanking {
  /** Player ID */
  id: number;
  /** Low-resolution position X (0,0 while dead or spectating) */
  x: number;
  /** Low-resolution position Y */
  y: number;
}

/** The leaderboard and the minimap positions, sent periodically. */
export interface ServerScoreBoardMessage {
  c: typeof SERVER_PACKETS.SCORE_BOARD;
  /** Scores, sorted */
  data: ScoreBoardData[];
  /** Minimap positions */
  rankings: ScoreBoardRanking[];
}

/** One row of the FFA detailed scoreboard. */
export interface ScoreDetailedScore {
  /** Player ID */
  id: number;
  /** Level */
  level: number;
  /** Score */
  score: number;
  /** Kills */
  kills: number;
  /** Deaths */
  deaths: number;
  /** Damage dealt */
  damage: number;
  /** Ping */
  ping: number;
}

/** Every player's statistics in FFA. */
export interface ServerScoreDetailedMessage {
  c: typeof SERVER_PACKETS.SCORE_DETAILED;
  /** Rows, sorted */
  scores: ScoreDetailedScore[];
}

/** One row of the CTF detailed scoreboard. */
export interface ScoreDetailedCtfScore {
  /** Player ID */
  id: number;
  /** Level */
  level: number;
  /** Flag captures */
  captures: number;
  /** Score */
  score: number;
  /** Kills */
  kills: number;
  /** Deaths */
  deaths: number;
  /** Damage dealt */
  damage: number;
  /** Ping */
  ping: number;
}

/** Every player's statistics in CTF. */
export interface ServerScoreDetailedCtfMessage {
  c: typeof SERVER_PACKETS.SCORE_DETAILED_CTF;
  /** Rows, sorted */
  scores: ScoreDetailedCtfScore[];
}

/** One row of the BTR detailed scoreboard. */
export interface ScoreDetailedBtrScore {
  /** Player ID */
  id: number;
  /** Level */
  level: number;
  /** Alive */
  alive: boolean;
  /** Rounds won */
  wins: number;
  /** Score */
  score: number;
  /** Kills */
  kills: number;
  /** Deaths */
  deaths: number;
  /** Damage dealt */
  damage: number;
  /** Ping */
  ping: number;
}

/** Every player's statistics in BTR. */
export interface ServerScoreDetailedBtrMessage {
  c: typeof SERVER_PACKETS.SCORE_DETAILED_BTR;
  /** Rows, sorted */
  scores: ScoreDetailedBtrScore[];
}

// =============================================================================
// Server messages
// =============================================================================

/** A message from the server to show the player. */
export interface ServerServerMessageMessage {
  c: typeof SERVER_PACKETS.SERVER_MESSAGE;
  /** Alert or info */
  type: SERVER_MESSAGE_TYPES;
  /** How long to show it, in milliseconds */
  duration: number;
  /** Message HTML */
  text: string;
}

/** A JSON payload whose shape the type gives: BTR or CTF end-of-match data, or a switch-game suggestion. */
export interface ServerServerCustomMessage {
  c: typeof SERVER_PACKETS.SERVER_CUSTOM;
  /** BTR end, CTF end or a switch-game suggestion */
  type: SERVER_CUSTOM_TYPES;
  /** The JSON */
  data: string;
}

// =============================================================================
// Sync service, server to server
// =============================================================================

/** Challenges a sync connection to authenticate. */
export interface ServerSyncAuthMessage {
  c: typeof SERVER_PACKETS.SYNC_AUTH;
  /** Randomly generated challenge */
  challenge: string;
}

/** Initialises a sync connection. */
export interface ServerSyncInitMessage {
  c: typeof SERVER_PACKETS.SYNC_INIT;
  /** Next sequence number the game server knows */
  sequence: number;
  /** Current time at the game server, in milliseconds since the Unix epoch */
  timestamp: number;
}

/** Subscribes to an object on a sync connection, or unsubscribes. */
export interface ServerSyncSubscribeMessage {
  c: typeof SERVER_PACKETS.SYNC_SUBSCRIBE;
  /** Subscribe (true) or unsubscribe (false) */
  active: boolean;
  /** Object type */
  type: string;
  /** Object ID */
  id: string;
}

/** An object's data, from the game server to the sync service. */
export interface ServerSyncUpdateMessage {
  c: typeof SERVER_PACKETS.SYNC_UPDATE;
  /** Sequence number, always increasing */
  sequence: number;
  /** Object type */
  type: string;
  /** Object ID */
  id: string;
  /** Object data as JSON */
  data: string;
  /** Update time, in milliseconds since the Unix epoch */
  timestamp: number;
  /** The event behind the update, as JSON */
  event: string;
}

/** Every packet a server sends, told apart by c. */
export type ServerMessage =
  | ServerLoginMessage
  | ServerBackupMessage
  | ServerPingMessage
  | ServerPingResultMessage
  | ServerAckMessage
  | ServerErrorMessage
  | ServerCommandReplyMessage
  | ServerPlayerNewMessage
  | ServerPlayerLeaveMessage
  | ServerPlayerUpdateMessage
  | ServerPlayerFireMessage
  | ServerPlayerHitMessage
  | ServerPlayerRespawnMessage
  | ServerPlayerFlagMessage
  | ServerPlayerKillMessage
  | ServerPlayerUpgradeMessage
  | ServerPlayerTypeMessage
  | ServerPlayerPowerupMessage
  | ServerPlayerLevelMessage
  | ServerPlayerReteamMessage
  | ServerGameFlagMessage
  | ServerGameSpectateMessage
  | ServerGamePlayersAliveMessage
  | ServerGameFirewallMessage
  | ServerEventRepelMessage
  | ServerEventBoostMessage
  | ServerEventBounceMessage
  | ServerEventStealthMessage
  | ServerEventLeaveHorizonMessage
  | ServerMobUpdateMessage
  | ServerMobUpdateStationaryMessage
  | ServerMobDespawnMessage
  | ServerMobDespawnCoordsMessage
  | ServerChatPublicMessage
  | ServerChatTeamMessage
  | ServerChatSayMessage
  | ServerChatWhisperMessage
  | ServerChatVoteMutePassedMessage
  | ServerChatVoteMutedMessage
  | ServerScoreUpdateMessage
  | ServerScoreBoardMessage
  | ServerScoreDetailedMessage
  | ServerScoreDetailedCtfMessage
  | ServerScoreDetailedBtrMessage
  | ServerServerMessageMessage
  | ServerServerCustomMessage
  | ServerSyncAuthMessage
  | ServerSyncInitMessage
  | ServerSyncSubscribeMessage
  | ServerSyncUpdateMessage;
